import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ArrowUpRight, Bookmark, Check, Plus, Search } from "lucide-react";
import { getProspectionLists, addEstablishmentsToList, createProspectionList } from "../../interfaces/prospectionLists";
import { IProspectionListSummary } from "../../interfaces/prospectionList";
import { useProspectionListCounts, formatListCount } from "../../hooks/useProspectionListCounts";

interface AddToListPickerProps {
    // One establishment (the per-row + button) or many (bulk-select action bar).
    establishmentIds: string[];
    /** Receives the list the establishments went into. */
    onAdded?: (list: { _id: string; name: string }) => void;
    // Render prop for the trigger — receives an onClick handler to attach.
    children: (props: { onClick: (e: React.MouseEvent) => void }) => React.ReactNode;
}

export function AddToListPicker({ establishmentIds, onAdded, children }: AddToListPickerProps) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const [lists, setLists] = useState<IProspectionListSummary[] | null>(null);
    const [search, setSearch] = useState("");
    const [addingId, setAddingId] = useState<string | null>(null);
    const [creating, setCreating] = useState(false);
    const [successId, setSuccessId] = useState<string | null>(null);
    const [successName, setSuccessName] = useState("");
    const [skippedMessage, setSkippedMessage] = useState<string | null>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    const counts = useProspectionListCounts((lists ?? []).map(l => l._id));

    useEffect(() => {
        if (!open) return;
        getProspectionLists().then(setLists).catch(() => setLists([]));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (
                panelRef.current && !panelRef.current.contains(e.target as Node) &&
                anchorRef.current && !anchorRef.current.contains(e.target as Node)
            ) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [open]);

    const handleOpen = (e: React.MouseEvent) => {
        e.stopPropagation();
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        const panelWidth = 260;
        const overflowsRight = rect.left + panelWidth > window.innerWidth;
        setPos({
            top: rect.bottom + 4,
            left: overflowsRight ? rect.right - panelWidth : rect.left,
        });
        setSearch("");
        setSuccessId(null);
        setSkippedMessage(null);
        setOpen(true);
    };

    const handleAddExisting = async (list: IProspectionListSummary) => {
        setAddingId(list._id);
        setSkippedMessage(null);
        try {
            const updated = await addEstablishmentsToList(list._id, establishmentIds);
            setSuccessId(list._id);
            setSuccessName(list.name);
            if (updated.skippedCount) {
                setSkippedMessage(
                    `${updated.skippedCount} établissement${updated.skippedCount > 1 ? "s" : ""} non ajouté${updated.skippedCount > 1 ? "s" : ""} — limite de 2 000 par liste atteinte.`
                );
            }
            onAdded?.({ _id: list._id, name: list.name });
        } catch (e) {
            console.error("Failed to add establishments to list:", e);
        } finally {
            setAddingId(null);
        }
    };

    const handleCreate = async () => {
        const name = search.trim();
        if (!name) return;
        setCreating(true);
        try {
            const created = await createProspectionList({
                name,
                type: "static",
                filters: { establishmentIds },
            });
            setLists((prev) => [created, ...(prev || [])]);
            setSuccessId(created._id);
            setSuccessName(created.name);
            setSearch("");
            onAdded?.({ _id: created._id, name: created.name });
        } catch (e) {
            console.error("Failed to create list:", e);
        } finally {
            setCreating(false);
        }
    };

    const filtered = (lists || []).filter((l) => l.name.toLowerCase().includes(search.trim().toLowerCase()));
    const exactMatch = (lists || []).some((l) => l.name.toLowerCase() === search.trim().toLowerCase());

    return (
        <>
            <div ref={anchorRef} className="inline-flex items-center">
                {children({ onClick: handleOpen })}
            </div>

            {open && pos && ReactDOM.createPortal(
                <div
                    ref={panelRef}
                    style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
                    className="w-[260px] bg-surface border border-border rounded-2xl shadow-float"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="px-3 py-2 border-b border-border flex items-center gap-1.5">
                        <Bookmark className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                        <span className="text-xs font-semibold text-ink">
                            Ajouter à une liste{establishmentIds.length > 1 ? ` (${establishmentIds.length})` : ""}
                        </span>
                    </div>

                    <div className="px-2 pt-2">
                        <div className="flex items-center gap-1.5 px-2 py-1.5 bg-surface-sunken rounded-lg">
                            <Search className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                            <input
                                autoFocus
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && !exactMatch && handleCreate()}
                                placeholder="Rechercher ou créer…"
                                className="flex-1 bg-transparent text-xs outline-none text-ink placeholder:text-ink-faint min-w-0"
                            />
                        </div>
                    </div>

                    <div className="max-h-56 overflow-y-auto ds-scrollbar py-1.5 px-1">
                        {lists === null ? (
                            <p className="px-2 py-2 text-xs text-ink-faint">Chargement…</p>
                        ) : filtered.length === 0 ? (
                            <p className="px-2 py-2 text-xs text-ink-faint">
                                {lists.length === 0
                                    ? "Aucune liste — tapez un nom ci-dessus pour en créer une"
                                    : "Aucun résultat"}
                            </p>
                        ) : (
                            filtered.map((list) => (
                                <button
                                    key={list._id}
                                    type="button"
                                    disabled={addingId === list._id}
                                    onClick={() => handleAddExisting(list)}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-surface-sunken transition-colors duration-150 disabled:opacity-50"
                                >
                                    <span className="flex-1 min-w-0 truncate text-xs text-ink">{list.name}</span>
                                    <span className="text-[10px] text-ink-faint shrink-0">
                                        {formatListCount(counts[list._id])}
                                    </span>
                                    {successId === list._id ? (
                                        <Check className="w-3.5 h-3.5 text-success shrink-0" />
                                    ) : addingId === list._id ? (
                                        <span className="w-3.5 h-3.5 border-2 border-border-strong border-t-ink rounded-full motion-safe:animate-spin shrink-0" />
                                    ) : null}
                                </button>
                            ))
                        )}
                    </div>

                    {/* Right after an add: a way to go and see it, in a new tab so the current page stays */}
                    {successId && (
                        <div className="px-3 py-2 border-t border-border flex items-center gap-2">
                            <Check className="w-3.5 h-3.5 text-success shrink-0" />
                            <span className="flex-1 min-w-0 truncate text-xs text-ink-muted">Ajouté à « {successName} »</span>
                            <a
                                href={`/prospection/${successId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="shrink-0 inline-flex items-center gap-0.5 text-xs font-medium text-ink hover:underline"
                            >
                                Voir dans ma liste
                                <ArrowUpRight className="w-3 h-3" />
                            </a>
                        </div>
                    )}

                    {skippedMessage && (
                        <div className="px-3 py-2 border-t border-border">
                            <p className="text-[10px] text-warning leading-snug">{skippedMessage}</p>
                        </div>
                    )}

                    {search.trim() && !exactMatch && (
                        <div className="border-t border-border p-1.5">
                            <button
                                type="button"
                                disabled={creating}
                                onClick={handleCreate}
                                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left hover:bg-surface-sunken transition-colors duration-150 disabled:opacity-50"
                            >
                                <Plus className="w-3.5 h-3.5 text-accent shrink-0" />
                                <span className="text-xs text-ink truncate">
                                    {creating ? "Création…" : <>Créer <strong>"{search.trim()}"</strong></>}
                                </span>
                            </button>
                        </div>
                    )}
                </div>,
                document.body
            )}
        </>
    );
}
